(() => {
  'use strict';

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeDrawer = null;
  let drawerTrigger = null;
  let drawerInertElements = [];
  let cartOperationQueue = Promise.resolve();
  const pendingCartForms = new WeakSet();
  const cartChangeTimeouts = new WeakMap();

  const findDrawer = (reference) => {
    if (!reference) return null;
    const id = reference.replace(/^#/, '');
    return [...document.querySelectorAll('[data-drawer], .drawer')].find(
      (drawer) => drawer.id === id || drawer.dataset.drawer === id
    );
  };

  const setDrawerBackgroundInert = (drawer) => {
    drawerInertElements.forEach((element) => {
      element.inert = false;
    });
    drawerInertElements = [];

    let current = drawer;
    while (current?.parentElement) {
      const parent = current.parentElement;
      [...parent.children].forEach((sibling) => {
        if (sibling === current || sibling.inert) return;
        sibling.inert = true;
        drawerInertElements.push(sibling);
      });
      if (parent === document.body) break;
      current = parent;
    }
  };

  const clearDrawerBackgroundInert = () => {
    drawerInertElements.forEach((element) => {
      element.inert = false;
    });
    drawerInertElements = [];
  };

  const getDrawerTrigger = (preferred) => {
    if (preferred?.isConnected) return preferred;
    return (
      document.querySelector('[aria-controls="CartDrawer"]') ||
      document.querySelector('[data-drawer-open]')
    );
  };

  const openDrawer = (drawer, trigger, initialFocusTarget) => {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);

    activeDrawer = drawer;
    drawerTrigger = getDrawerTrigger(trigger || document.activeElement);
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    drawerTrigger?.setAttribute('aria-expanded', 'true');
    setDrawerBackgroundInert(drawer);

    const initialFocus =
      (initialFocusTarget?.isConnected && initialFocusTarget) ||
      drawer.querySelector('[data-drawer-initial-focus]') ||
      drawer.querySelector(focusableSelector);
    window.requestAnimationFrame(() => initialFocus?.focus());
    document.dispatchEvent(new CustomEvent('theme:drawer:open', { detail: { drawer } }));
  };

  const closeDrawer = (drawer = activeDrawer, restoreFocus = true) => {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
    });
    if (activeDrawer === drawer) activeDrawer = null;
    document.body.classList.toggle('drawer-open', Boolean(activeDrawer));
    if (!activeDrawer) clearDrawerBackgroundInert();
    const returnFocus = getDrawerTrigger(drawerTrigger);
    if (restoreFocus && returnFocus?.isConnected) returnFocus.focus();
    document.dispatchEvent(new CustomEvent('theme:drawer:close', { detail: { drawer } }));
  };

  const trapDrawerFocus = (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...activeDrawer.querySelectorAll(focusableSelector)].filter(
      (element) => element.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleQuantity = (button) => {
    const container = button.closest('[data-quantity], .quantity');
    const input = container?.querySelector('input[type="number"]');
    if (!input || input.disabled) return;

    const action = button.dataset.quantityAction || button.dataset.quantityButton;
    const step = Number(input.step) || 1;
    const minimum = input.min === '' ? 0 : Number(input.min);
    const maximum = input.max === '' ? Infinity : Number(input.max);
    const current = Number(input.value) || minimum;
    const direction = action === 'minus' || action === 'decrease' ? -1 : 1;
    input.value = String(Math.min(maximum, Math.max(minimum, current + direction * step)));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const replaceSection = (sectionId, html) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const wrapperId = `shopify-section-${sectionId}`;
    const current =
      document.getElementById(wrapperId) || document.querySelector(`[data-section-id="${sectionId}"]`);
    const replacement =
      parsed.getElementById(wrapperId) || parsed.querySelector(`[data-section-id="${sectionId}"]`);

    if (current && replacement) {
      current.replaceWith(replacement);
      return replacement;
    } else if (current) {
      current.innerHTML = html;
      return current;
    }
    return null;
  };

  const getCartSections = (form) => {
    const sections = new Set(
      (form.dataset.cartSections || '')
        .split(',')
        .map((section) => section.trim())
        .filter(Boolean)
    );
    document
      .querySelectorAll('[data-cart-drawer][data-section-id], [data-main-cart][data-section-id]')
      .forEach((section) => sections.add(section.dataset.sectionId));
    return [...sections];
  };

  const formatMoney = (cents) => {
    const value = Number(cents) || 0;
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const format = window.theme?.moneyFormat || 'Rs. {{amount}}';
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  };

  const getSelectedVariant = () => {
    const root = document.querySelector('[data-product-root]');
    const variantInput = root?.querySelector('[data-variant-id]');
    const variantsElement = root?.querySelector('[data-product-variants]');
    if (!variantInput || !variantsElement) return null;
    try {
      const variants = JSON.parse(variantsElement.textContent);
      return variants.find((variant) => String(variant.id) === String(variantInput.value)) || null;
    } catch {
      return null;
    }
  };

  const updateBundleTotals = (form) => {
    if (!form) return;
    const current = getSelectedVariant();
    const currentPrice = current?.price || 0;
    const currentPriceNode = form.querySelector('[data-bundle-current-price]');
    if (currentPriceNode && current?.priceFormatted) {
      currentPriceNode.textContent = current.priceFormatted;
    }
    let total = currentPrice;
    form.querySelectorAll('[data-bundle-item]').forEach((input) => {
      if (input.checked) total += Number(input.dataset.bundlePrice) || 0;
    });
    const totalNode = form.querySelector('[data-bundle-total]');
    if (totalNode) totalNode.textContent = formatMoney(total);
  };

  const submitBundleForm = async (form) => {
    if (pendingCartForms.has(form)) return;
    pendingCartForms.add(form);
    const submitButton = form.querySelector('[data-bundle-submit], [type="submit"]');
    const errorContainer = form.querySelector('[data-cart-error]');
    const current = getSelectedVariant();
    const items = [];
    if (current?.id) items.push({ id: Number(current.id), quantity: 1 });
    form.querySelectorAll('[data-bundle-item]:checked').forEach((input) => {
      items.push({ id: Number(input.value), quantity: 1 });
    });
    if (errorContainer) {
      errorContainer.hidden = true;
      errorContainer.textContent = '';
    }
    if (items.length === 0) {
      pendingCartForms.delete(form);
      return;
    }

    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    const returnFocus = getDrawerTrigger(submitButton);

    return enqueueCartOperation(async () => {
      try {
        const cartAdd = window.theme?.routes?.cartAdd || '/cart/add';
        const ajaxAction = cartAdd.endsWith('.js') ? cartAdd : `${cartAdd}.js`;
        const response = await fetch(ajaxAction, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: getCartSections(form),
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);

        Object.entries(result.sections || {}).forEach(([sectionId, html]) => {
          replaceSection(sectionId, html);
        });
        await refreshCartCount();

        const cartDrawer = findDrawer('CartDrawer') || document.querySelector('[data-cart-drawer]');
        if (cartDrawer) openDrawer(cartDrawer, returnFocus);
      } catch (error) {
        const message = error.message || window.theme?.strings?.cartError || 'Unable to update the cart.';
        if (errorContainer?.isConnected) {
          errorContainer.textContent = message;
          errorContainer.hidden = false;
          errorContainer.focus();
        }
      } finally {
        pendingCartForms.delete(form);
        if (submitButton?.isConnected) {
          submitButton.removeAttribute('aria-busy');
          submitButton.disabled = false;
        }
      }
    });
  };

  const enqueueCartOperation = (operation) => {
    const queued = cartOperationQueue.then(operation, operation);
    cartOperationQueue = queued.catch(() => {});
    return queued;
  };

  const refreshCartCount = async () => {
    const root = window.theme?.routes?.root || window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
      count.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const label =
        cart.item_count === 1
          ? link.dataset.cartLabelOne
          : (link.dataset.cartLabelOther || '').replace('[count]', cart.item_count);
      if (label) link.setAttribute('aria-label', label);
    });
    document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { cart } }));
  };

  const submitCartForm = async (form) => {
    if (pendingCartForms.has(form)) return;
    pendingCartForms.add(form);
    const submitButton = form.querySelector('[type="submit"]');
    const errorContainer = form.querySelector('[data-cart-error]');
    const sections = getCartSections(form);
    const formData = new FormData(form);
    formData.set('sections', sections.join(','));
    formData.set('sections_url', window.location.pathname);
    const returnFocus = getDrawerTrigger(submitButton);

    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    if (errorContainer) {
      errorContainer.hidden = true;
      errorContainer.textContent = '';
    }

    return enqueueCartOperation(async () => {
      try {
        const formAction = form.action || window.theme?.routes?.cartAdd || '/cart/add';
        const ajaxAction = formAction.replace(/\/cart\/add$/, '/cart/add.js');
        const response = await fetch(ajaxAction, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);

        Object.entries(result.sections || {}).forEach(([sectionId, html]) => {
          replaceSection(sectionId, html);
        });
        await refreshCartCount();

        const cartDrawer = findDrawer('CartDrawer') || document.querySelector('[data-cart-drawer]');
        if (cartDrawer) openDrawer(cartDrawer, returnFocus);
      } catch (error) {
        const message = error.message || window.theme?.strings?.cartError || 'Unable to update the cart.';
        if (errorContainer?.isConnected) {
          errorContainer.textContent = message;
          errorContainer.hidden = false;
          errorContainer.focus();
        }
        document.dispatchEvent(new CustomEvent('theme:cart:error', { detail: { error, form } }));
      } finally {
        pendingCartForms.delete(form);
        if (submitButton?.isConnected) {
          submitButton.removeAttribute('aria-busy');
          submitButton.disabled = false;
        }
      }
    });
  };

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.limit = this.dataset.limit || '6';
      this.abortController = null;
      this.activeIndex = -1;
      if (!this.input || !this.results) return;

      let timeout;
      this.input.addEventListener('input', () => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => this.search(), 250);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        if (['ArrowDown', 'ArrowUp'].includes(event.key) && !this.results.hidden) {
          event.preventDefault();
          this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
          return;
        }
        if (event.key === 'Enter' && this.activeIndex >= 0) {
          const option = this.getOptions()[this.activeIndex];
          if (option) {
            event.preventDefault();
            option.click();
          }
        }
      });
      this.results.addEventListener('mousemove', (event) => {
        const option = event.target.closest('[role="option"]');
        const options = this.getOptions();
        const index = options.indexOf(option);
        if (index >= 0) this.setActiveOption(index);
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
    }

    getOptions() {
      return [...this.results.querySelectorAll('[role="option"]')];
    }

    setActiveOption(index) {
      const options = this.getOptions();
      if (!options.length) return;
      this.activeIndex = ((index % options.length) + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        const selected = optionIndex === this.activeIndex;
        option.setAttribute('aria-selected', String(selected));
        option.classList.toggle('predictive-search__link--active', selected);
      });
      const activeOption = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }

    moveActiveOption(direction) {
      const start = this.activeIndex < 0 && direction < 0 ? 0 : this.activeIndex;
      this.setActiveOption(start + direction);
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.close();
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const requestController = this.abortController;
      this.input.setAttribute('aria-busy', 'true');

      const root = window.theme?.routes?.root || window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.dataset.types || 'product,collection,page,article',
        'resources[limit]': this.limit,
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const configuredEndpoint = window.theme?.routes?.predictiveSearch || `${root}search/suggest`;
        const endpoint = configuredEndpoint.endsWith('.json')
          ? configuredEndpoint
          : `${configuredEndpoint}.json`;
        const response = await fetch(`${endpoint}?${params}`, {
          signal: requestController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
        const payload = await response.json();
        this.render(payload.resources?.results || {}, query);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        if (this.abortController === requestController) {
          this.input.removeAttribute('aria-busy');
        }
      }
    }

    render(groups, query) {
      const fragment = document.createDocumentFragment();
      let resultCount = 0;

      Object.entries(groups).forEach(([type, items]) => {
        if (!Array.isArray(items) || !items.length) return;
        const group = document.createElement('div');
        group.className = 'predictive-search__group';
        group.setAttribute('role', 'group');
        const heading = document.createElement('h3');
        heading.className = 'predictive-search__heading';
        heading.id = `${this.results.id}-${type}-heading`;
        group.setAttribute('aria-labelledby', heading.id);
        heading.textContent =
          window.theme?.strings?.searchGroups?.[type] ||
          type.charAt(0).toUpperCase() + type.slice(1);
        const list = document.createElement('ul');
        list.className = 'predictive-search__list';
        list.setAttribute('role', 'presentation');

        items.forEach((item) => {
          const listItem = document.createElement('li');
          listItem.setAttribute('role', 'presentation');
          const link = document.createElement('a');
          link.className = 'predictive-search__link';
          link.id = `${this.results.id}-option-${resultCount}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          link.tabIndex = -1;
          link.href = item.url;
          const imageUrl = typeof item.image === 'string' ? item.image : item.image?.url;
          if (imageUrl) {
            const image = document.createElement('img');
            image.className = 'predictive-search__image';
            image.src = imageUrl;
            image.alt = '';
            image.width = 48;
            image.height = 48;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.fetchPriority = 'low';
            link.append(image);
          }
          const content = document.createElement('span');
          const title = document.createElement('span');
          title.className = 'predictive-search__title';
          title.textContent = item.title;
          content.append(title);
          if (item.price) {
            const price = document.createElement('span');
            price.className = 'predictive-search__price';
            price.textContent = item.price;
            content.append(price);
          }
          link.append(content);
          listItem.append(link);
          list.append(listItem);
          resultCount += 1;
        });
        group.append(heading, list);
        fragment.append(group);
      });

      if (!resultCount) {
        const empty = document.createElement('p');
        empty.className = 'predictive-search__empty';
        empty.textContent = window.theme?.strings?.searchNoResults || 'No results found.';
        fragment.append(empty);
      }

      const allResults = document.createElement('a');
      allResults.className = 'predictive-search__all';
      allResults.id = `${this.results.id}-option-${resultCount}`;
      allResults.setAttribute('role', 'option');
      allResults.setAttribute('aria-selected', 'false');
      allResults.tabIndex = -1;
      allResults.href = `${window.theme?.routes?.root || '/'}search?q=${encodeURIComponent(
        query
      )}&type=product%2Ccollection%2Cpage%2Carticle&options%5Bprefix%5D=last`;
      allResults.textContent = window.theme?.strings?.searchViewAll || 'View all results';
      fragment.append(allResults);

      this.results.replaceChildren(fragment);
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (this.status) {
        this.status.textContent = (window.theme?.strings?.searchResults || '{{ count }} results').replace(
          '{{ count }}',
          resultCount
        );
      }
    }

    close() {
      this.abortController?.abort();
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      if (this.status) this.status.textContent = '';
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.innerHTML.trim()) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          fetch(this.dataset.url)
            .then((response) => (response.ok ? response.text() : ''))
            .then((text) => {
              if (!text) return;
              const parsed = new DOMParser().parseFromString(text, 'text/html');
              const recommendations = parsed.querySelector('product-recommendations');
              if (recommendations?.innerHTML.trim()) this.innerHTML = recommendations.innerHTML;
            })
            .catch(() => {});
        },
        { rootMargin: '300px' }
      );
      observer.observe(this);
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  const initializeProduct = (root) => {
    if (root.dataset.productInitialized === 'true') return;
    const variantsElement = root.querySelector('[data-product-variants]');
    if (!variantsElement) return;

    let variants;
    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch {
      return;
    }

    root.dataset.productInitialized = 'true';
    const groups = [...root.querySelectorAll('[data-option-position]')];
    const variantInput = root.querySelector('[data-variant-id]');
    const price = root.querySelector('[data-product-price]');
    const sku = root.querySelector('[data-product-sku]');
    const availability = root.querySelector('[data-product-availability]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
    const announcement = root.querySelector('[data-product-announcement]');

    const appendVisuallyHidden = (parent, text) => {
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = text;
      parent.append(hidden);
    };

    const renderPrice = (variant) => {
      if (!price || !variant) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'price';
      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        appendVisuallyHidden(
          wrapper,
          window.theme?.strings?.regularPrice || 'Regular price'
        );
        const compareAt = document.createElement('s');
        compareAt.textContent = variant.compareAtPriceFormatted;
        wrapper.append(compareAt);
        appendVisuallyHidden(wrapper, window.theme?.strings?.salePrice || 'Sale price');
      }
      const currentPrice = document.createElement('span');
      currentPrice.textContent = variant.priceFormatted;
      wrapper.append(currentPrice);

      if (variant.unitPriceFormatted && variant.unitPriceReferenceUnit) {
        const unitPrice = document.createElement('small');
        appendVisuallyHidden(
          unitPrice,
          window.theme?.strings?.unitPrice || 'Unit price'
        );
        unitPrice.append(document.createTextNode(`${variant.unitPriceFormatted} / `));
        if (variant.unitPriceReferenceValue && variant.unitPriceReferenceValue !== 1) {
          unitPrice.append(document.createTextNode(String(variant.unitPriceReferenceValue)));
        }
        unitPrice.append(document.createTextNode(variant.unitPriceReferenceUnit));
        wrapper.append(unitPrice);
      }
      price.replaceChildren(wrapper);
    };

    const update = (userInitiated = false) => {
      const options = groups.map((group) => {
        const selected = group.querySelector('[data-option-value]:checked');
        const selectedLabel = group.querySelector('[data-selected-option]');
        if (selectedLabel) selectedLabel.textContent = selected?.value || '';
        return selected?.value;
      });
      const variant = groups.length
        ? variants.find((candidate) =>
            candidate.options.every((option, index) => option === options[index])
          )
        : variants.find((candidate) => String(candidate.id) === String(variantInput?.value)) ||
          variants.find((candidate) => candidate.available) ||
          variants[0] ||
          null;
      const addLabel = window.theme?.strings?.addToCart || 'Add to cart';
      const soldLabel = window.theme?.strings?.soldOut || 'Sold out';
      const unavailableLabel = window.theme?.strings?.unavailable || 'Unavailable';
      const purchaseLabel = variant
        ? variant.available
          ? addLabel
          : soldLabel
        : unavailableLabel;
      const canPurchase = Boolean(variant?.available);

      if (variant) {
        variantInput.disabled = false;
        variantInput.value = variant.id;
      } else {
        variantInput.value = '';
        variantInput.disabled = true;
      }
      if (addButton) {
        addButton.disabled = !canPurchase;
        const label = addButton.querySelector('[data-add-to-cart-text]');
        if (label) label.textContent = purchaseLabel;
        else addButton.textContent = purchaseLabel;
      }
      if (dynamicCheckout) {
        dynamicCheckout.hidden = !canPurchase;
        dynamicCheckout.setAttribute('aria-hidden', String(!canPurchase));
      }

      renderPrice(variant);
      if (sku) {
        sku.hidden = !variant?.sku;
        const value = sku.querySelector('span');
        if (value) value.textContent = variant?.sku || '';
      }
      if (availability) {
        if (variant?.available) {
          availability.hidden = true;
          availability.textContent = '';
        } else {
          availability.hidden = false;
          availability.textContent = variant ? soldLabel : unavailableLabel;
        }
      }
      if (announcement && userInitiated) {
        announcement.textContent = variant
          ? `${variant.priceFormatted}. ${availability?.textContent || purchaseLabel}`
          : unavailableLabel;
      }

      groups.forEach((group, index) => {
        group.querySelectorAll('[data-option-value]').forEach((input) => {
          const matchingVariants = variants.filter(
            (candidate) =>
              candidate.options[index] === input.value &&
              candidate.options.every(
                (option, optionIndex) =>
                  optionIndex === index || option === options[optionIndex]
              )
          );
          const combinationExists = matchingVariants.length > 0;
          const combinationAvailable = matchingVariants.some((candidate) => candidate.available);
          input.disabled = false;
          input.toggleAttribute('data-option-invalid', !combinationExists);
          input.toggleAttribute(
            'data-option-unavailable',
            combinationExists && !combinationAvailable
          );
        });
      });

      document.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { variant } }));

      if (window.history.replaceState) {
        const url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id);
        else url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
    };

    groups.forEach((group) => group.addEventListener('change', () => update(true)));
    update();
  };

  const initializeGallery = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    if (!gallery || gallery.dataset.galleryInitialized === 'true') return;

    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    gallery.dataset.galleryInitialized = 'true';
    if (!track || slides.length < 2) return;

    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const prev = gallery.querySelector('[data-gallery-prev]');
    const next = gallery.querySelector('[data-gallery-next]');
    let index = Math.max(
      0,
      slides.findIndex((slide) => slide.hasAttribute('data-selected-media'))
    );

    const syncUi = () => {
      slides.forEach((slide, slideIndex) => {
        slide.toggleAttribute('data-selected-media', slideIndex === index);
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === index;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-selected', String(selected));
      });
    };

    const goTo = (nextIndex, smooth = true) => {
      index = (nextIndex + slides.length) % slides.length;
      const slide = slides[index];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto'
      });
      syncUi();
    };

    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    thumbs.forEach((thumb, thumbIndex) => {
      thumb.addEventListener('click', () => goTo(thumbIndex));
    });

    track.addEventListener(
      'scroll',
      () => {
        const nearest = slides.reduce(
          (best, slide, slideIndex) => {
            const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
            return distance < best.distance ? { index: slideIndex, distance } : best;
          },
          { index, distance: Infinity }
        );
        if (nearest.index !== index) {
          index = nearest.index;
          syncUi();
        }
      },
      { passive: true }
    );

    const viewport = gallery.querySelector('[data-gallery-viewport]');
    viewport?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(index - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(index + 1);
      }
    });

    document.addEventListener('theme:variant:change', (event) => {
      if (!root.contains(gallery)) return;
      const mediaId = event.detail?.variant?.featuredMediaId;
      if (!mediaId) return;
      const slideIndex = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (slideIndex >= 0) goTo(slideIndex);
    });

    goTo(index, false);
  };

  const initializeBundleDeals = (root) => {
    const deals = root.querySelector('[data-bundle-deals]');
    if (!deals) return;

    const variantInput = root.querySelector('[data-variant-id]');
    const price = root.querySelector('[data-product-price]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addLabel = addButton?.querySelector('[data-add-to-cart-text]');

    const apply = (input) => {
      if (!input) return;
      if (variantInput) variantInput.value = input.value;
      const variantsElement = root.querySelector('[data-product-variants]');
      let variant = null;
      try {
        const variants = JSON.parse(variantsElement?.textContent || '[]');
        variant = variants.find((entry) => String(entry.id) === String(input.value)) || null;
      } catch {
        variant = null;
      }
      if (variant && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      document.dispatchEvent(new CustomEvent('theme:variant:change', { detail: { variant } }));
      if (price) {
        const wrapper = document.createElement('div');
        wrapper.className = 'price';
        const percent = Number(input.dataset.percent) || 0;
        if (percent > 0 && input.dataset.regularFormatted) {
          const compare = document.createElement('s');
          compare.textContent = input.dataset.regularFormatted;
          wrapper.append(compare);
        }
        const current = document.createElement('span');
        current.textContent = input.dataset.priceFormatted || '';
        wrapper.append(current);
        if (percent > 0 && input.dataset.saveFormatted) {
          const saving = document.createElement('p');
          saving.className = 'bundle-deals__saving';
          saving.textContent = `Save ${input.dataset.saveFormatted} (${percent}% off)`;
          price.replaceChildren(wrapper, saving);
        } else {
          price.replaceChildren(wrapper);
        }
      }
      const available = input.dataset.available !== 'false';
      if (addButton) {
        addButton.disabled = !available;
        if (addLabel) addLabel.textContent = available
          ? window.theme?.strings?.addToCart || 'Add to cart'
          : window.theme?.strings?.soldOut || 'Sold out';
      }
      const codButton = root.querySelector('[data-cod-checkout]');
      const codPrice = root.querySelector('[data-cod-price]');
      if (codButton) codButton.disabled = !available;
      if (codPrice) codPrice.textContent = input.dataset.priceFormatted || '';
    };

    deals.addEventListener('change', (event) => {
      const input = event.target.closest('[data-bundle-deal]');
      if (input) apply(input);
    });
    apply(deals.querySelector('[data-bundle-deal]:checked') || deals.querySelector('[data-bundle-deal]'));
  };

  const reviewBadgeMarkup = () => {
    return (
      '<a href="#reviews" class="skadeas-rating" aria-label="Rated 4.8 out of 5 based on 598 reviews">' +
      '<span class="skadeas-stars" aria-hidden="true">' +
      '<span class="skadeas-stars-empty">★★★★★</span>' +
      '<span class="skadeas-stars-filled" style="width: 96%">★★★★★</span>' +
      '</span>' +
      '<span class="skadeas-rating-score">4.8</span>' +
      '<span class="skadeas-rating-count">(598 reviews)</span>' +
      '</a>'
    );
  };

  const ensureReviewBadge = (root) => {
    if (!root.querySelector('[data-bundle-deals]')) return;
    const title = root.querySelector('.product-info h1');
    let badge = root.querySelector('.skadeas-rating') || root.querySelector('.product-review-badge');
    if (badge && !badge.classList.contains('skadeas-rating')) {
      badge.outerHTML = reviewBadgeMarkup();
      badge = root.querySelector('.skadeas-rating');
    }
    if (badge && title) {
      if (badge.previousElementSibling !== title) title.after(badge);
      return;
    }
    if (badge) return;
    if (title) {
      title.insertAdjacentHTML('afterend', reviewBadgeMarkup());
      return;
    }
    const price = root.querySelector('.product-info__price');
    if (price) price.insertAdjacentHTML('beforebegin', reviewBadgeMarkup());
  };

  const initializeProductPage = (root) => {
    if (!root) return;
    initializeGallery(root);
    initializeProduct(root);
    initializeBundleDeals(root);
    ensureReviewBadge(root);
  };

  document.querySelectorAll('[data-product-root]').forEach(initializeProductPage);
  document.addEventListener('shopify:section:load', (event) => {
    initializeProductPage(event.target.querySelector('[data-product-root]') || event.target);
  });

  const initializeReviewSection = (root) => {
    if (!root) return;
    const form = root.querySelector('[data-review-form]');
    const moreButton = root.querySelector('[data-review-more]');

    if (moreButton) {
      moreButton.addEventListener('click', () => {
        root.querySelectorAll('[data-review-card][hidden]').forEach((card) => {
          card.hidden = false;
        });
        moreButton.remove();
      });
    }

    if (!form || form.dataset.reviewBound) return;
    form.dataset.reviewBound = 'true';
    form.addEventListener('submit', async (event) => {
      const fields = form.querySelector('[data-review-fields]');
      const success = form.querySelector('[data-review-success]');
      const honeypot = form.querySelector('[name="contact[website]"]');
      if (honeypot?.value) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const formData = new FormData(form);
      const rating = formData.get('review_rating') || '';
      const city = formData.get('contact[city]') || '';
      const product = formData.get('contact[product]') || '';
      const body = formData.get('contact[body]') || '';
      formData.set(
        'contact[body]',
        `Product review (${rating}/5)\nProduct: ${product}\nCity: ${city}\n\n${body}`
      );

      if (fields) fields.hidden = true;
      if (success) {
        success.hidden = false;
        success.focus();
      }
      fetch(form.action || '/contact', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'text/html' }
      }).catch(() => {});
    });
  };

  document.querySelectorAll('[data-reviews-root]').forEach(initializeReviewSection);
  document.addEventListener('shopify:section:load', (event) => {
    initializeReviewSection(event.target.querySelector('[data-reviews-root]') || event.target);
  });

  const submitCartMutation = async (form, submitter) => {
    const errorContainer = form.querySelector('[data-cart-error]');
    const sections = getCartSections(form);
    const formData = new FormData(form);
    if (submitter?.name) formData.set(submitter.name, submitter.value);
    formData.set('sections', sections.join(','));
    formData.set('sections_url', window.location.pathname);
    const activeLineInput =
      form.querySelector('[data-line-key]:focus') || form.querySelector('[data-line-key]');
    const activeLineKey = activeLineInput?.dataset.lineKey;
    const drawerWasOpen =
      form.closest('[data-cart-drawer]')?.getAttribute('aria-hidden') === 'false';
    const returnFocus = getDrawerTrigger(document.activeElement);

    let endpoint = form.action;
    if (form.matches('[data-cart-change-form]')) endpoint = endpoint.replace(/\/cart\/change$/, '/cart/change.js');
    else endpoint = endpoint.replace(/\/cart\/?$/, '/cart/update.js');

    form.setAttribute('aria-busy', 'true');
    return enqueueCartOperation(async () => {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);
        Object.entries(result.sections || {}).forEach(([sectionId, html]) =>
          replaceSection(sectionId, html)
        );
        await refreshCartCount();

        const updatedLineInput = activeLineKey
          ? [...document.querySelectorAll('[data-line-key]')].find(
              (input) => input.dataset.lineKey === activeLineKey
            )
          : null;
        if (drawerWasOpen) {
          openDrawer(findDrawer('CartDrawer'), returnFocus, updatedLineInput);
        } else if (updatedLineInput) {
          window.requestAnimationFrame(() => updatedLineInput.focus());
        }
      } catch (error) {
        const currentError =
          (errorContainer?.isConnected && errorContainer) ||
          document.querySelector('[data-main-cart] [data-cart-error]') ||
          document.querySelector('[data-cart-drawer] [data-cart-error]');
        if (currentError) {
          currentError.textContent = error.message || window.theme?.strings?.cartError;
          currentError.hidden = false;
          currentError.focus();
        }
      } finally {
        if (form.isConnected) form.removeAttribute('aria-busy');
      }
    });
  };

  const selectedProductVariantId = () => {
    const checked = document.querySelector('[data-bundle-deals] [name="id"]:checked');
    if (checked?.value) return checked.value;
    return document.querySelector('[data-variant-id]')?.value || '';
  };

  const addSelectedProductToCart = async () => {
    const id = selectedProductVariantId();
    if (!id) throw new Error(window.theme?.strings?.cartError || 'Unable to update the cart.');
    const quantity = Number(document.querySelector('form.product-form [name="quantity"]')?.value) || 1;
    const cartAdd = window.theme?.routes?.cartAdd || '/cart/add';
    const ajaxAction = cartAdd.endsWith('.js') ? cartAdd : `${cartAdd}.js`;
    const response = await fetch(ajaxAction, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: Number(id) || id, quantity })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.description || result.message || window.theme?.strings?.cartError);
  };

  const bankOfferCloseMarkup =
    '<button type="button" class="atebar-bt-modal__close" data-atebar-bt-close="1" data-atebar-bt-skip="1" aria-label="Close">' +
    '<svg class="icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18"/></svg>' +
    '</button>';

  const dismissBankOfferModal = () => {
    document.getElementById('atebar-bt-modal')?.remove();
  };

  const ensureBankOfferModalClose = (root = document) => {
    const overlay =
      root.id === 'atebar-bt-modal' ? root : root.querySelector?.('#atebar-bt-modal') || document.getElementById('atebar-bt-modal');
    if (!overlay) return;
    const card = overlay.querySelector('.atebar-bt-modal__card');
    if (!card || card.querySelector('[data-atebar-bt-close]')) return;
    card.insertAdjacentHTML('afterbegin', bankOfferCloseMarkup);
  };

  const showBankOfferModal = ({ onAccept, onDecline }) => {
    if (document.getElementById('atebar-bt-modal')) return;
    const cfg = window.__ATEBAR_BT__?.config || {};
    const pct = Math.max(0, Number(cfg.discount_percent) || 5);
    const overlay = document.createElement('div');
    overlay.id = 'atebar-bt-modal';
    overlay.className = 'atebar-bt-modal';
    overlay.innerHTML =
      '<div class="atebar-bt-modal__card" role="dialog" aria-modal="true" aria-labelledby="atebar-bt-offer-title">' +
      bankOfferCloseMarkup +
      '<p class="atebar-bt-modal__eyebrow">Limited offer</p>' +
      `<h3 id="atebar-bt-offer-title">Save ${pct}% with Bank Transfer</h3>` +
      `<p class="atebar-bt-modal__copy">Pay by bank transfer to unlock ${pct}% off this order.</p>` +
      '<div class="atebar-bt-modal__row">' +
      `<button type="button" class="atebar-bt-btn atebar-bt-btn--embed" data-accept="1">Pay with Bank Transfer · ${pct}% off</button>` +
      '<button type="button" class="atebar-bt-modal__skip" data-decline="1">Continue without discount</button>' +
      '</div></div>';
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) overlay.remove();
    });
    overlay.querySelector('[data-accept]')?.addEventListener('click', () => {
      overlay.remove();
      onAccept();
    });
    overlay.querySelector('[data-decline]')?.addEventListener('click', () => {
      overlay.remove();
      onDecline();
    });
    document.body.appendChild(overlay);
    overlay.querySelector('[data-accept]')?.focus();
  };

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) ensureBankOfferModalClose(node);
      }
    }
  }).observe(document.body, { childList: true });

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('[data-cod-checkout]');
      if (!button || button.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const goToCodCheckout = async () => {
        button.disabled = true;
        try {
          await addSelectedProductToCart();
          window.location.href = '/checkout';
        } catch (error) {
          button.disabled = false;
          const errorContainer = document.querySelector('[data-cart-error]');
          if (errorContainer) {
            errorContainer.textContent = error.message || window.theme?.strings?.cartError || 'Unable to update the cart.';
            errorContainer.hidden = false;
            errorContainer.focus();
          }
        }
      };

      const cfg = window.__ATEBAR_BT__?.config;
      if (cfg && cfg.discount_enabled === false) {
        goToCodCheckout();
        return;
      }

      showBankOfferModal({
        onAccept: () => {
          const bankButton = document.querySelector('[data-atebar-bt-btn]');
          if (bankButton) {
            bankButton.click();
            return;
          }
          addSelectedProductToCart()
            .then(() => {
              window.location.href = '/pages/bank-transfer';
            })
            .catch(() => {});
        },
        onDecline: goToCodCheckout
      });
    },
    true
  );

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-drawer-open]');
    if (openButton) {
      const reference = openButton.getAttribute('aria-controls') || openButton.dataset.drawerOpen;
      const drawer = findDrawer(reference);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer, openButton);
      }
      return;
    }

    const closeButton = event.target.closest('[data-drawer-close], .drawer__overlay');
    if (closeButton) {
      event.preventDefault();
      closeDrawer(closeButton.closest('[data-drawer], .drawer') || activeDrawer);
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-button], [data-quantity-action]');
    if (quantityButton) {
      event.preventDefault();
      handleQuantity(quantityButton);
    }

    const searchToggle = event.target.closest('[data-search-toggle]');
    if (searchToggle) {
      const panel = document.getElementById(searchToggle.getAttribute('aria-controls'));
      if (!panel) return;
      const willOpen = panel.hasAttribute('hidden');
      panel.toggleAttribute('hidden', !willOpen);
      searchToggle.setAttribute('aria-expanded', String(willOpen));
      searchToggle.setAttribute(
        'aria-label',
        willOpen
          ? window.theme?.strings?.closeSearch || 'Close search'
          : window.theme?.strings?.openSearch || 'Open search'
      );
      if (willOpen) panel.querySelector('[data-search-input], input[type="search"]')?.focus();
      return;
    }

    const openSearch = document.querySelector('[data-search-toggle][aria-expanded="true"]');
    if (openSearch) {
      const panel = document.getElementById(openSearch.getAttribute('aria-controls'));
      if (panel && !panel.contains(event.target) && event.target !== openSearch) {
        panel.setAttribute('hidden', '');
        openSearch.setAttribute('aria-expanded', 'false');
        openSearch.setAttribute('aria-label', window.theme?.strings?.openSearch || 'Open search');
      }
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      const close = event.target.closest?.('[data-atebar-bt-close]');
      if (!close) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      dismissBankOfferModal();
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (document.getElementById('atebar-bt-modal')) {
      event.preventDefault();
      dismissBankOfferModal();
      return;
    }
    const openSearch = document.querySelector('[data-search-toggle][aria-expanded="true"]');
    if (!openSearch) return;
    const panel = document.getElementById(openSearch.getAttribute('aria-controls'));
    panel?.setAttribute('hidden', '');
    openSearch.setAttribute('aria-expanded', 'false');
    openSearch.setAttribute('aria-label', window.theme?.strings?.openSearch || 'Open search');
    openSearch.focus();
  });
  document.addEventListener('keydown', trapDrawerFocus);
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-cart-form]');
    if (!form || !window.fetch || !window.FormData) return;
    const submitter = event.submitter;
    if (submitter?.closest?.('[data-atebar-bt-btn], [data-atebar-bt-slot], [data-dynamic-checkout]')) return;
    if (submitter?.name && submitter.name !== 'add') return;
    event.preventDefault();
    submitCartForm(form);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-bundle-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    submitBundleForm(form);
  });

  document.addEventListener('change', (event) => {
    const item = event.target.closest('[data-bundle-item]');
    const form = item?.closest('[data-bundle-form]');
    if (form) updateBundleTotals(form);
  });

  document.addEventListener('theme:variant:change', () => {
    document.querySelectorAll('[data-bundle-form]').forEach((form) => updateBundleTotals(form));
  });

  document.querySelectorAll('[data-bundle-form]').forEach((form) => updateBundleTotals(form));

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('[data-cart-change-form], [data-cart-update-form]');
      if (!form || !window.fetch || !window.FormData) return;
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const timeout = cartChangeTimeouts.get(form);
      if (timeout) window.clearTimeout(timeout);
      cartChangeTimeouts.delete(form);
      submitCartMutation(form, event.submitter);
    },
    true
  );

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-key]');
    const form = input?.closest('[data-cart-change-form], [data-cart-update-form]');
    if (!form) return;
    const currentTimeout = cartChangeTimeouts.get(form);
    if (currentTimeout) window.clearTimeout(currentTimeout);
    const timeout = window.setTimeout(() => {
      cartChangeTimeouts.delete(form);
      if (form.isConnected) submitCartMutation(form);
    }, 350);
    cartChangeTimeouts.set(form, timeout);
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (select) select.form?.submit();
  });
})();
